import React from 'react';
import {
  Box,
  Flex,
  getOpenNetworkTheme,
  OHeaderFive,
  Tooltip,
  OHeaderTwo,
} from '@lifechurch/react-ion';

import ChartComponent from '../../ChartComponent';
import Chart from 'chart.js';
import AnalyticsOverviewTrend from './AnalyticsOverviewTrend';
import { useTranslation } from 'react-i18next';

interface Props {
  chartdata: Chart.ChartData;
  prayerSessions: string;
  trend: number;
}

const viewersOptions: Chart.ChartOptions = {
  animation: { duration: 0 }, // meaning, don't animate.
  maintainAspectRatio: false,
  title: {
    display: false,
  },
  legend: {
    display: false,
  },
  scales: {
    yAxes: [
      {
        gridLines: {
          display: true,
          color: getOpenNetworkTheme().colors.gray10,
          zeroLineWidth: 2,
          tickMarkLength: 0,
          zeroLineColor: getOpenNetworkTheme().colors.gray10,
          drawBorder: false,
        },
        ticks: {
          beginAtZero: true,
          padding: 8,
          maxTicksLimit: 5,
          stepSize: 1,
          fontColor: getOpenNetworkTheme().colors.gray50,
          fontFamily: 'Proxima Nova',
          fontSize: 10.72,
        },
      },
    ],
    xAxes: [
      {
        gridLines: {
          display: false,
          color: getOpenNetworkTheme().colors.gray10,
        },
        ticks: {
          min: 0,
          beginAtZero: true,
          padding: 4,
          fontColor: getOpenNetworkTheme().colors.gray50,
          fontFamily: 'Proxima Nova',
          fontSize: 10.72,
          autoSkip: true,
          autoSkipPadding: 50,
          labelOffset: 10,
        },
      },
    ],
  },
  hover: {
    animationDuration: 0, // this affects the tooltips
  },
  tooltips: {
    backgroundColor: getOpenNetworkTheme().colors.gray100,
    borderColor: 'black',
    borderWidth: 0,
    titleFontColor: 'white',
    titleFontFamily: 'Proxima Nova',
    titleFontSize: 13.44,
    titleMarginBottom: 2,
    bodyFontColor: 'white',
    bodyFontFamily: 'Proxima Nova',
    bodyFontSize: 13.44,
    cornerRadius: getOpenNetworkTheme().radii[1],
    displayColors: false,
    caretSize: 0,
    xPadding: 8,
    yPadding: 6,
    callbacks: {
      title: function (tooltipItems, object) {
        // TODO: Maybe translate
        const dataindex = tooltipItems[0].index;
        if (object && object.labels && dataindex !== undefined) {
          return `${object.labels[dataindex] || '?'}`;
        }
        return '';
      },
    },
  },
};

const AnalyticsOverviewPrayerBox: React.FC<Props> = ({ chartdata, prayerSessions, trend }) => {
  const { t } = useTranslation(['analytics_overview', 'common']);

  return (
    <Box width='100%' position='relative'>
      <Flex marginBlockEnd={5} justifyContent='space-between'>
        <Flex marginBlockStart={1} alignSelf='baseline'>
          <OHeaderFive marginInlineEnd={1}>{t('prayer_sessions')}</OHeaderFive>
          <Tooltip content={t('prayer_sessions_tooltip')} placement='top' />
        </Flex>
        <Flex>
          {Number.isFinite(trend) && <AnalyticsOverviewTrend value={trend} />}
          <OHeaderTwo>{prayerSessions}</OHeaderTwo>
        </Flex>
      </Flex>
      <Box width='100%' height='140px' position='relative'>
        <Box width='inherit' height='inherit' position='absolute'>
          {/* A chart takes its size from its container. But, it resizes with
             a delay, so its size must not be used to determine the overall size;
             it must follow the size it is given. Thus the position=absolute. */}
          {/* Unrelated to the above: "key" must be present otherwise React
             usually won't re-render the chart when the dataset changes. */}
          <ChartComponent
            charttype='bar'
            chartdata={chartdata}
            chartoptions={viewersOptions}
            key={JSON.stringify(
              (chartdata && chartdata.datasets && chartdata.datasets[0].data) || 'x'
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AnalyticsOverviewPrayerBox;
